import React, { useEffect } from "react";
import { Info, Search, Plus, Trash2 } from "lucide-react";
import { TrackItem } from "../AudioEngineer/sections/TrackItem";
import SettingsIcon from "../../../../assets/images/icons/settings.svg";
import { Tooltip } from "../../../../components/Tooltip";
import Dialog from "../../../../components/Dialogbox";
import { useDispatch, useSelector } from "react-redux";
import MusicLoader from "../../../../helper/MusicLoader";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { deleteMasterTrack, fetchMastersLibrary, searchMastersLibrary } from "../../../../redux/actions/audioEngineer";
import { PricingPlanModal } from "../../../../components/PricingPlanModal";
import { showToast } from "../../../../helper/ShowToast";
import Settings from "../AudioEngineer/sections/Settings";
import { TrackTable } from "../AudioEngineer/sections/TrackTable";

const MastersLibrary = ({ addNew, handleSettings, showHeader = true }) => {
  const dispatch = useDispatch()
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [showPricingPlanModal, setShowPricingPlanModal] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false)
  const [selectedTracks, setSelectedTracks] = React.useState([]);
  const localGenre = localStorage.getItem('genre')
  const localLoudness = localStorage.getItem('loudness')
  const localSampleRate = localStorage.getItem('sampleRate')
  const [genre, setGenre] = React.useState(localGenre || "ROCK_INDIE");
  const [loudness, setLoudness] = React.useState(localLoudness || "MEDIUM");
  const [sampleRate, setSampleRate] = React.useState(localSampleRate || "48000")
  const [query, setQuery] = React.useState("");
  const [debouncedQuery, setDebouncedQuery] = React.useState("");
  const audioEngineerReducer = useSelector((state) => state.audioEngineer);
  const { hasActiveSubscription } = useSelector((state) => state.billing);
  const { storage } = audioEngineerReducer?.mastersLibrary || {};

  // Debounce effect (waits 500ms after user stops typing)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(query);
    }, 500);

    return () => {
      clearTimeout(handler); // clear timeout if user types again
    };
  }, [query]);

  useEffect(() => {
    if (!debouncedQuery) return;
    dispatch(searchMastersLibrary(toast, query))
  }, [debouncedQuery]);

  useEffect(() => {
    dispatch(fetchMastersLibrary(toast, setIsLoading))
  }, [])

  const handleDialog = () => {
    setIsDialogOpen(!isDialogOpen);
  };

  const saveSettings = () => {
    if (!hasActiveSubscription) {
      showToast("info", "Please subscribe to change settings.");
      setIsDialogOpen(!isDialogOpen);
      setShowPricingPlanModal(true);   // Open subscription modal
      return;
    } else {
      setIsDialogOpen(!isDialogOpen);
      localStorage.setItem("genre", genre)
      localStorage.setItem("loudness", loudness)
      localStorage.setItem("sampleRate", sampleRate)
      handleSettings({ genre: genre, loudness: loudness })
    }
  }

  const tracks = audioEngineerReducer?.mastersLibrary?.data || [];
  const isAllSelected = tracks.length > 0 && selectedTracks.length === tracks.length;

  const handleBulkDelete = () => {
    if (!selectedTracks || selectedTracks.length === 0) return;
    const ids = selectedTracks.join(",");
    dispatch(deleteMasterTrack(toast, ids, setIsLoading))
    if(!isLoading){
      setSelectedTracks([]);
    }
  };

  const handleSelectAll = () => {
    const tracks = audioEngineerReducer?.mastersLibrary?.data || [];
    if (selectedTracks.length === tracks.length) {
      // Deselect all
      setSelectedTracks([]);
    } else {
      // Select all
      const allTrackIds = tracks.map(track => track._id);
      setSelectedTracks(allTrackIds);
    }
  };

  return (
    <div className="bg-[#00067e] rounded-2xl p-6 overflow-x-auto p-4 lg:p-5">
      {/* Header */}
      <header className="flex flex-wrap items-center gap-4 mb-4">
        {/* <div className="flex items-center gap-3 flex-wrap">
          <Info size={24} className="text-theme_clr2" />
          <h1 className="text-white text-2xl font-semibold">Masters Library</h1>
          {storage?.totalMB &&
            <div className="flex items-center gap-2 text-gray-300 text-sm">
              Storage
              <span className="text-sm">{storage?.totalMB} MB used</span>
            </div>
          }
        </div> */}
        {showHeader &&
          (!isAllSelected && selectedTracks.length == 0 &&
            <>
              <Info size={24} className="text-theme_clr2" />
              <h1 className="text-white text-2xl font-semibold">Masters Library</h1>
            </>
          )
        }
        <div className="flex items-center gap-3 flex-wrap">
          {/* Select All Button */}
          {tracks.length > 0 && (
            <button
              onClick={handleSelectAll}
              className="bg-theme_clr2 hover:bg-theme_clr4 text-white px-4 py-2 rounded-full flex items-center gap-2 transition-colors text-sm"
            >
              {/* <input
                                type="checkbox"
                                checked={isAllSelected}
                                onChange={handleSelectAll}
                                className="rounded border-gray-400 bg-transparent text-[#FA6138] focus:ring-[#FA6138] cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                            /> */}
              <span>{isAllSelected ? 'Deselect All' : 'Select All'}</span>
            </button>
          )}
          {/* Show storage or action buttons based on selection */}
          {selectedTracks.length > 0 ? (
            <>
              <button
                onClick={handleBulkDelete}
                className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-full flex items-center gap-2 transition-colors text-sm"
              >
                <Trash2 size={16} />
                <span>Delete</span>
              </button>
              <div className="text-gray-300 text-sm">
                {selectedTracks.length} selected
              </div>
            </>
          ) : (
            storage?.totalMB && (
              <div className="flex items-center gap-2 text-gray-300 text-sm">
                Storage
                <span className="text-sm">{storage?.totalMB} MB used</span>
              </div>
            )
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap ml-auto">
          {/* Search bar */}
          <div className="relative w-full md:w-[320px]">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Search by name"
              onChange={(e) => setQuery(e.target.value)}
              className="bg-transparent border border-gray-400 text-white rounded-full pl-12 pr-6 py-2 w-full"
            />
          </div>

          {/* New Button */}
          <button onClick={addNew} className="bg-[#FA6138] hover:bg-[#ff724d] text-white px-4 py-2 rounded-full flex items-center gap-2 transition-colors">
            <Plus size={20} />
            <span>New</span>
          </button>

          {/* Settings Button */}
          <Tooltip
            text={"Settings"}
            style={{ bg: "bg-white", text: "text-theme_clr1" }}
          >
            <button
              onClick={handleDialog}
              className="bg-white flex items-center justify-center rounded-full hover:bg-theme_clr2 h-[40px] w-[40px]"
            >
              <img src={SettingsIcon} className="block" />
            </button>
          </Tooltip>
        </div>
      </header>
      {isLoading
        ? <MusicLoader />
        : <div className="overflow-x-auto">
          <table className="w-full text-left border-separate border-spacing-y-2">
            <TrackTable
              selectedTracks={selectedTracks}
              setSelectedTracks={setSelectedTracks}
            />
          </table>
        </div>
      }
      {/* Settings Dialog */}
      <Dialog title={"Settings"} hasActiveSubscription={hasActiveSubscription} isOpen={isDialogOpen} onClose={handleDialog} onSave={saveSettings} leftButton={'Cancel'} rightButton={'Save'}>
        <hr className="border-gray-700 mb-3" />
        {/* Mastering Preferences */}
        {!hasActiveSubscription &&
          <div className="mb-6">
            <h3 className="font-medium text-theme_clr2 mb-3">
              Mastering Preferences
            </h3>
            <div className="bg-theme_clr1 text-gray-300 p-4 rounded-xl flex flex-col gap-3">
              <p>
                <span className="mr-2">👑</span> Save your settings and speed up
                your workflow with a subscription.
              </p>
              <button className="bg-white text-[#0B1E3F] px-4 py-2 rounded-lg font-medium w-fit">
                Upgrade now
              </button>
            </div>
          </div>
        }
        <Settings
          genre={genre}
          setGenre={setGenre}
          loudness={loudness}
          setLoudness={setLoudness}
          sampleRate={sampleRate}
          setSampleRate={setSampleRate}
        />

        <hr className="border-gray-700" />
      </Dialog>
      <PricingPlanModal isOpen={showPricingPlanModal} onClose={() => setShowPricingPlanModal(false)} returnUrl={window.location.origin + "/admin/audio-engineer"} />
    </div>
  );
};

export default MastersLibrary;
